
import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useFirebase } from './hooks/useFirebase';
import { doc, onSnapshot, collection, query, where, orderBy, limit } from "firebase/firestore";
import { USERS_COLLECTION, NOTIFICATIONS_COLLECTION, PROPOSALS_COLLECTION } from './config/constants';
import { SIDEBAR_ADS } from './config/ads';

import LoginScreen from './screens/LoginScreen';
import WelcomeScreen from './screens/WelcomeScreen';
import RoleSelectionScreen from './screens/RoleSelectionScreen';
import SignUpScreen from './screens/SignUpScreen';
import PrintQuoteScreen from './screens/PrintQuoteScreen.jsx';
import InvoiceDetailScreen from './screens/InvoiceDetailScreen.jsx';
import NotificationsScreen from './screens/NotificationsScreen.jsx';
import ChatScreen from './screens/ChatScreen';
import MediaCaptureScreen from './screens/MediaCaptureScreen.jsx';
import AuthLoadingScreen from './screens/AuthLoadingScreen';
import MainLayout from './MainLayout.jsx';
import Toast from './components/common/Toast';
import ProposalsScreen from './screens/ProposalsScreen.jsx';
import TermosClienteScreen from './screens/TermosClienteScreen.jsx';
import TermosServiScreen from './screens/TermosServiScreen.jsx';
import PrivacidadeScreen from './screens/PrivacidadeScreen.jsx';
import PublicServiProfileScreen from './screens/PublicServiProfileScreen.jsx';
import NotificationPrompt from './components/common/NotificationPrompt';

// Admin Screens
import AdminDashboardScreen from './screens/admin/AdminDashboardScreen.jsx';
import AdminLoginScreen from './screens/admin/AdminLoginScreen.jsx';

const AppContext = createContext();
export const useAppContext = () => useContext(AppContext);

const AuthProvider = ({ children }) => {
    const [toast, setToast] = useState({ message: '', type: '' });
    const showMessage = useCallback((message, type = 'info') => setToast({ message, type }), []);
    const firebase = useFirebase(showMessage);

    const [fullUserData, setFullUserData] = useState(null);
    const [activeRole, setActiveRole] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [notifications, setNotifications] = useState([]);
    const [pendingProposalsCount, setPendingProposalsCount] = useState(0);

    useEffect(() => {
        if (firebase.isAuthReady) {
            if (firebase.user) {
                const unsub = onSnapshot(doc(firebase.db, USERS_COLLECTION, firebase.user.uid),
                    (doc) => {
                        if (doc.exists()) {
                            const data = doc.data();
                            setFullUserData(data);
                            const availableRoles = Object.keys(data.roles || {}).filter(r => data.roles[r]);

                            if (data.roles?.admin && availableRoles.length === 1) {
                                setActiveRole('admin');
                            } else {
                                const functionalRoles = availableRoles.filter(r => r !== 'admin');
                                if (functionalRoles.length === 1 && !activeRole) {
                                    setActiveRole(functionalRoles[0]);
                                }
                            }
                        } else {
                            setFullUserData(null);
                        }
                        setIsLoading(false);
                    },
                    () => setIsLoading(false)
                );
                return () => unsub();
            } else {
                setFullUserData(null);
                setActiveRole(null);
                setNotifications([]);
                setPendingProposalsCount(0);
                setIsLoading(false);
            }
        }
    }, [firebase.isAuthReady, firebase.user, firebase.db, firebase.requestNotificationPermission, activeRole]);

    useEffect(() => {
        if (!firebase.user?.uid || !activeRole || activeRole === 'admin') {
            setNotifications([]);
            return undefined;
        }

        const notificationsQuery = query(
            collection(firebase.db, NOTIFICATIONS_COLLECTION),
            where("userId", "==", firebase.user.uid),
            where("targetRole", "==", activeRole),
            where("status", "in", ["pending", "done"]),
            orderBy("createdAt", "desc"),
            limit(12)
        );

        const unsubNotifs = onSnapshot(notificationsQuery,
            (snapshot) => {
                const notifsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setNotifications(notifsData);
            },
            () => setNotifications([])
        );

        return () => unsubNotifs();
    }, [firebase.user, firebase.db, activeRole]);

    useEffect(() => {
        if (!firebase.user?.uid || activeRole !== 'client') {
            setPendingProposalsCount(0);
            return undefined;
        }

        const proposalsQuery = query(
            collection(firebase.db, PROPOSALS_COLLECTION),
            where("clientId", "==", firebase.user.uid),
            where("status", "==", "pending")
        );

        const unsubProposals = onSnapshot(proposalsQuery, (snapshot) => {
            setPendingProposalsCount(snapshot.size);
        });

        return () => unsubProposals();
    }, [firebase.user, firebase.db, activeRole]);


    const setActiveUserRole = useCallback((role) => {
        if (fullUserData?.roles?.[role]) {
            setActiveRole(role);
        }
    }, [fullUserData]);

    const value = {
        ...firebase,
        showMessage,
        fullUserData,
        activeRole,
        setActiveUserRole,
        isLoading,
        notifications,
        notificationCount: notifications.filter(n => n.status === 'pending').length,
        pendingProposalsCount
    };

    return (
        <AppContext.Provider value={value}>
            {children}
            <NotificationPrompt />
            <Toast message={toast.message} type={toast.type} onDismiss={() => setToast({ message: '' })} />
        </AppContext.Provider>
    );
};

const AppRoutes = () => {
    const { user, isLoading, fullUserData, activeRole } = useAppContext();

    if (isLoading) return <AuthLoadingScreen message="Sincronizando..." />;

    return (
        <Routes>
            <Route path="/termos/cliente" element={<TermosClienteScreen />} />
            <Route path="/termos/servi" element={<TermosServiScreen />} />
            <Route path="/privacidade" element={<PrivacidadeScreen />} />
            <Route path="/v/:serviId" element={<PublicServiProfileScreen />} />

            <Route path="/admin/login" element={(!user || !fullUserData?.roles?.admin) ? <AdminLoginScreen /> : <Navigate to="/admin" />} />
            <Route path="/admin" element={fullUserData?.roles?.admin ? <AdminDashboardScreen /> : <Navigate to="/admin/login" />} />

            {!user ? (
                <>
                    <Route path="/welcome" element={<WelcomeScreen />} />
                    <Route path="/login" element={<LoginScreen />} />
                    <Route path="/cadastro" element={<SignUpScreen />} />
                    <Route path="*" element={<Navigate to="/welcome" />} />
                </>
            ) : (
                <>
                    {fullUserData?.roles?.admin && !fullUserData?.roles?.client && !fullUserData?.roles?.servi && (
                        <Route path="*" element={<Navigate to="/admin" />} />
                    )}

                    <Route path="/adicionar-perfil" element={<SignUpScreen />} />
                    <Route path="/proposals" element={<ProposalsScreen />} />
                    <Route path="/print-quote" element={<PrintQuoteScreen />} />
                    <Route path="/invoice/:invoiceId" element={<InvoiceDetailScreen />} />
                    <Route path="/notifications" element={<NotificationsScreen />} />
                    <Route path="/chat/:chatId" element={<ChatScreen />} />
                    <Route path="/servi/chat/:chatId" element={<ChatScreen />} />
                    <Route path="/capture" element={<MediaCaptureScreen />} />

                    {(Object.keys(fullUserData?.roles || {}).filter(r => r !== 'admin').length > 1 && !activeRole) ? (
                        <Route path="*" element={<RoleSelectionScreen />} />
                    ) : (
                        <Route path="*" element={<MainLayout />} />
                    )}
                </>
            )}
        </Routes>
    );
};

// Componente AdCard OTIMIZADO: Aceita anúncios com ou sem link e proporção melhorada
const AdCard = ({ ad }) => {
    if (!ad) return null;

    const Content = () => (
        <div className="bg-white rounded-[2.5rem] p-5 shadow-md border border-gray-100 hover:shadow-xl transition-all active:scale-[0.98] animate-fade-in text-left">
            <h3 className={`font-black uppercase text-[11px] tracking-[0.15em] mb-4 ${ad.color || 'text-gray-900'}`}>{ad.title || 'Informativo'}</h3>

            <div className="aspect-[4/3] mb-4 rounded-[2rem] overflow-hidden bg-gray-100 shadow-inner">
                {ad.videoUrl ? (
                    <video src={ad.videoUrl} autoPlay muted loop playsInline className="w-full h-full object-cover" />
                ) : ad.imageUrl ? (
                    <img src={ad.imageUrl} alt={ad.title} className="w-full h-full object-cover" />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-300 font-bold text-[8px]">T-SERVI ADS</div>
                )}
            </div>

            <p className="text-[11px] font-bold text-gray-500 leading-tight px-1">{ad.description || ''}</p>
        </div>
    );

    if (!ad.link || ad.link.trim() === "") {
        return <Content />;
    }

    const isExternal = ad.link.startsWith('http');
    return (
        <a
            href={ad.link}
            target={isExternal ? "_blank" : "_self"}
            rel={isExternal ? "noopener noreferrer" : ""}
            className="block no-underline"
        >
            <Content />
        </a>
    );
};

const RotatingAds = ({ ads, title }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
        if (!ads || ads.length <= 1) return;

        const interval = setInterval(() => {
            setCurrentIndex((prevIndex) => (prevIndex + 1) % ads.length);
        }, 15000);

        return () => clearInterval(interval);
    }, [ads]);

    if (!ads || !Array.isArray(ads) || ads.length === 0) return null;

    return (
        <div className="space-y-5">
            <div className="flex justify-between items-center px-3">
                <h2 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.25em]">{title}</h2>
                {ads.length > 1 && (
                    <div className="flex space-x-1.5">
                        {ads.map((_, i) => (
                            <div key={i} className={`w-1.5 h-1.5 rounded-full transition-all duration-500 ${i === currentIndex ? 'bg-blue-500 w-4' : 'bg-gray-200'}`} />
                        ))}
                    </div>
                )}
            </div>
            <AdCard ad={ads[currentIndex]} />
        </div>
    );
};

const App = () => (
    <Router>
        <AuthProvider>
            <div className="min-h-screen bg-gray-200 flex justify-center items-start overflow-x-hidden">

                {/* BARRA ESQUERDA - Informativos e Dicas */}
                <div className="hidden lg:flex flex-col w-[320px] sticky top-0 h-screen p-5 space-y-10 overflow-y-auto no-scrollbar">
                    <RotatingAds ads={SIDEBAR_ADS?.left} title="Informativos" />

                    <div className="p-7 bg-blue-600 rounded-[2.5rem] text-white shadow-xl shadow-blue-200/50">
                        <p className="text-[10px] font-black uppercase tracking-widest mb-3">Dica Extra</p>
                        <p className="text-[12px] font-semibold leading-relaxed opacity-90">Mantenha seu T-SERVI atualizado para receber novos recursos.</p>
                    </div>
                </div>

                {/* MOLDURA DO APLICATIVO */}
                <div className="App font-sans w-full max-w-lg h-screen bg-white shadow-2xl flex flex-col relative overflow-hidden">
                    <AppRoutes />
                </div>

                {/* BARRA DIREITA - Parceiros e Administração */}
                <div className="hidden lg:flex flex-col w-[320px] sticky top-0 h-screen p-5 space-y-10 overflow-y-auto no-scrollbar">
                    <RotatingAds ads={SIDEBAR_ADS?.right} title="Parceiros" />

                    <div className="p-7 bg-slate-800 rounded-[2.5rem] text-blue-400 border border-blue-900/30 shadow-lg">
                        <p className="text-[9px] font-black uppercase tracking-[0.25em] mb-3">Área Administrativa</p>
                        <p className="text-[11px] font-bold text-gray-400 leading-relaxed">Gerencie usuários e propostas com total segurança.</p>
                    </div>

                    <div className="p-7 bg-emerald-600 rounded-[2.5rem] text-white shadow-xl shadow-emerald-200/50">
                        <p className="text-[10px] font-black uppercase tracking-widest mb-3">Suporte Oficial</p>
                        <p className="text-[12px] font-semibold leading-relaxed opacity-90">Dúvidas? Fale com nossa equipe técnica pelo chat.</p>
                    </div>
                </div>

            </div>
        </AuthProvider>
    </Router>
);

export default App;
